// Секция «Календарь листингов»: calendar lunar cu listările descoperite pe cele 6 burse.

import { el, clear, debounce } from '../core/util.js';
import { iconMoneda, iconBursa } from '../ui/coin-icon.js';
import { icon } from '../ui/icons.js';
import { baseTicker, dropdown, toast } from '../ui/components.js';
import { EXCHANGE_LIST } from '../core/exchanges.js';

const WEEKDAYS = ['ПН', 'ВТ', 'СР', 'ЧТ', 'ПТ', 'СБ', 'ВС'];
const MONTHS = [
  'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
  'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь',
];
const MAX_PER_DAY = 3;

export class ListingsSection {
  constructor(app) {
    this.app = app;
    this.items = [];
    this.cursor = new Date();
    this.search = '';
    this.status = 'all';       // all | upcoming | done
    this.marketFilter = 'all'; // all | spot | futures
    this.exchangeFilter = 'all';
    this.expanded = new Set();
  }

  async mount(host) {
    this.root = el('div', { class: 'listings-section' });
    host.append(this.root);
    this.#build();
    await this.#fetch();
  }

  unmount() {
    this.root?.remove();
  }

  #build() {
    this.titleEl = el('span', { class: 'lst-month', text: this.#monthTitle() });
    this.gridEl = el('div', { class: 'lst-grid' });
    this.countsEl = el('span', { class: 'lst-counts' });

    this.root.append(el('div', { class: 'lst-card' }, [
      el('div', { class: 'lst-head' }, [
        el('h2', { class: 'lst-title', text: 'Календарь листингов' }),
        this.countsEl,
      ]),

      el('div', { class: 'lst-filters' }, [
        el('div', { class: 'lst-search' }, [
          el('input', {
            class: 'search-input',
            placeholder: 'Поиск монет',
            oninput: debounce((e) => { this.search = e.target.value.trim().toUpperCase(); this.#render(); }, 180),
          }),
          icon('search', 13),
        ]),

        this.#pillGroup('status', [
          ['all', 'Все'], ['upcoming', 'Предстоящие'], ['done', 'Состоялись'],
        ]),
        this.#pillGroup('marketFilter', [
          ['all', 'Все'], ['spot', 'Спот'], ['futures', 'Фьючерсы'],
        ]),

        dropdown({
          label: 'Все биржи',
          items: [{ value: 'all', label: 'Все биржи' },
            ...EXCHANGE_LIST.map((e) => ({ value: e.key, label: e.label,
              nod: () => iconBursa(e.key, 15) }))],
          value: 'all',
          onSelect: (value) => { this.exchangeFilter = value; this.#render(); },
        }),
      ]),

      el('div', { class: 'lst-nav' }, [
        el('button', { class: 'lst-nav-btn', onclick: () => this.#shiftMonth(-1) }, [icon('chevronLeft', 14)]),
        this.titleEl,
        el('button', { class: 'lst-nav-btn', onclick: () => this.#shiftMonth(1) }, [icon('chevronRight', 14)]),
        el('button', {
          class: 'lst-today', text: 'Сегодня',
          onclick: () => { this.cursor = new Date(); this.#render(); },
        }),
        el('div', { class: 'lst-legend' }, [
          el('span', { class: 'lst-dot upcoming' }), el('span', { text: 'Предстоит' }),
          el('span', { class: 'lst-dot done' }), el('span', { text: 'Состоялся' }),
        ]),
      ]),

      el('div', { class: 'lst-weekdays' }, WEEKDAYS.map((d) => el('span', { text: d }))),
      this.gridEl,
    ]));
  }

  #pillGroup(field, options) {
    const group = el('div', { class: 'lst-pills' });
    for (const [id, label] of options) {
      group.append(el('button', {
        class: `lst-pill${this[field] === id ? ' active' : ''}`,
        text: label,
        onclick: (event) => {
          this[field] = id;
          [...group.children].forEach((c) => c.classList.remove('active'));
          event.currentTarget.classList.add('active');
          this.#render();
        },
      }));
    }
    return group;
  }

  #monthTitle() {
    return `${MONTHS[this.cursor.getMonth()]} ${this.cursor.getFullYear()}`;
  }

  #shiftMonth(delta) {
    this.cursor = new Date(this.cursor.getFullYear(), this.cursor.getMonth() + delta, 1);
    this.#render();
  }

  async #fetch() {
    try {
      const resp = await fetch('/api/listings?limit=5000');
      const data = await resp.json();
      // `discovered_at === 0` = simbol din baza inițială, fără dată reală de listare.
      this.items = (data.items || []).filter((i) => i.discovered_at > 0);
      this.total = data.total || 0;
      this.#render();
    } catch (err) {
      console.warn('[listings]', err);
      toast('Не удалось загрузить листинги', 'error');
    }
  }

  #filtered() {
    const now = Date.now() / 1000;
    return this.items.filter((item) => {
      if (this.search && !item.symbol.includes(this.search)) return false;
      if (this.marketFilter !== 'all' && item.market !== this.marketFilter) return false;
      if (this.exchangeFilter !== 'all' && item.exchange !== this.exchangeFilter) return false;
      if (this.status === 'upcoming' && item.discovered_at <= now) return false;
      if (this.status === 'done' && item.discovered_at > now) return false;
      return true;
    });
  }

  #render() {
    if (!this.gridEl) return;
    this.titleEl.textContent = this.#monthTitle();

    const items = this.#filtered();
    const now = Date.now() / 1000;
    const upcoming = items.filter((i) => i.discovered_at > now).length;

    clear(this.countsEl);
    this.countsEl.append(
      el('span', { class: 'lst-count', text: `Состоялись ${items.length - upcoming}` }),
      el('span', { class: 'lst-count', text: `Предстоящие ${upcoming}` }),
    );

    // Grupăm pe zi calendaristică.
    const byDay = new Map();
    for (const item of items) {
      const date = new Date(item.discovered_at * 1000);
      const key = `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
      if (!byDay.has(key)) byDay.set(key, []);
      byDay.get(key).push(item);
    }

    const year = this.cursor.getFullYear();
    const month = this.cursor.getMonth();
    const first = new Date(year, month, 1);
    // Luni = prima coloană.
    const offset = (first.getDay() + 6) % 7;
    const start = new Date(year, month, 1 - offset);
    const today = new Date();

    clear(this.gridEl);
    for (let i = 0; i < 42; i++) {
      const date = new Date(start.getFullYear(), start.getMonth(), start.getDate() + i);
      const key = `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
      const dayItems = byDay.get(key) || [];
      const outside = date.getMonth() !== month;
      const isToday = date.toDateString() === today.toDateString();

      const cell = el('div', {
        class: `lst-day${outside ? ' outside' : ''}${isToday ? ' today' : ''}`,
      }, [
        el('div', { class: 'lst-day-head' }, [
          el('span', { class: 'lst-day-num', text: String(date.getDate()) }),
          dayItems.length ? el('span', { class: 'lst-day-count', text: String(dayItems.length) }) : null,
        ]),
      ]);

      const expanded = this.expanded.has(key);
      const shown = expanded ? dayItems : dayItems.slice(0, MAX_PER_DAY);
      for (const item of shown) {
        const time = new Date(item.discovered_at * 1000)
          .toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
        cell.append(el('div', {
          class: `lst-item${item.discovered_at > now ? ' upcoming' : ''}`,
          title: `${item.symbol} · ${item.exchange} · ${item.market}`,
        }, [
          iconBursa(item.exchange, 18),
          el('span', { class: 'lst-item-body' }, [
            iconMoneda(item.symbol, 18),
            el('span', { class: 'lst-item-sym', text: baseTicker(item.symbol) }),
            el('span', {
              class: 'lst-item-meta',
              text: `${item.exchange.toUpperCase()} · ${item.market === 'spot' ? 'Spot' : 'Futures'} · ${time}`,
            }),
          ]),
        ]));
      }

      if (dayItems.length > MAX_PER_DAY) {
        cell.append(el('button', {
          class: 'lst-more',
          text: expanded ? 'свернуть' : `+${dayItems.length - MAX_PER_DAY} ещё`,
          onclick: () => {
            if (expanded) this.expanded.delete(key); else this.expanded.add(key);
            this.#render();
          },
        }));
      }
      this.gridEl.append(cell);
    }

    if (!this.items.length) {
      this.gridEl.append(el('div', {
        class: 'lst-hint',
        text: 'История листингов накапливается с момента первого запуска сервера — '
            + 'новые монеты появятся здесь автоматически.',
      }));
    }
  }
}
